import { FC, useContext } from 'react';

import { H3, NumberInput, Select } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import { ETariffType } from 'types';
import { getSeconds } from 'utils';

import s from '../Tariff.module.scss';
import { ITariffTypeSectionProps } from './interfaces';

export const TariffTypeSection: FC<ITariffTypeSectionProps> = ({
  control,
  tariffType,
  getValues,
}) => {
  const { t } = useContext(LocalizationContext);

  return (
    <div className={s['form-block']}>
      <H3 className={s.header}>{t('tariff.type')}</H3>
      <div className={cn(s.fields, s.type)}>
        <Controller
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              title={t('type')}
              disableClear
              bordered
              options={[
                {
                  title: t('tariff.type.fix'),
                  value: ETariffType.FIXED,
                },
                {
                  title: t('tariff.type.minute'),
                  value: ETariffType.PER_MINUTE,
                },
                {
                  title: t('tariff.type.package'),
                  value: ETariffType.PACKET,
                },
                {
                  title: t('tariff.type.pause'),
                  value: ETariffType.PAUSABLE,
                },
              ]}
            />
          )}
          name="type"
        />
        {tariffType !== ETariffType.PACKET && (
          <div className={s.duration_block}>
            <Controller
              control={control}
              render={({ field, fieldState: { error } }) => (
                <NumberInput
                  {...field}
                  title={t('duration')}
                  placeholder="1"
                  error={error?.message}
                  errorWrapperClassName={s.duration}
                  disabled={tariffType === ETariffType.PER_MINUTE}
                  bordered
                />
              )}
              name="duration"
              rules={{
                validate: (val) => {
                  if (val === '') {
                    return t('errors.required');
                  }

                  const durationPeriod = getValues('duration_type');
                  const seconds =
                    getSeconds(val || 0, durationPeriod as string) ?? 0;

                  if (seconds / 60 < 1) {
                    return t('errors.min', 1, t('time.contracted.minutes'));
                  }

                  if (seconds > 2592000) {
                    return t('errors.max', 30, t('time.contracted.days'));
                  }

                  return undefined;
                },
              }}
            />
            <Controller
              control={control}
              render={({ field: { ref, ...field } }) => (
                <Select
                  {...field}
                  title={t('tariff.format')}
                  bordered
                  options={[
                    {
                      title: t('tariff.format.minutes'),
                      value: 'minutes',
                    },
                    {
                      title: t('tariff.format.hours'),
                      value: 'hours',
                    },
                    {
                      title: t('tariff.format.days'),
                      value: 'days',
                    },
                    {
                      title: t('tariff.format.weeks'),
                      value: 'weeks',
                    },
                  ]}
                  disabled={tariffType === ETariffType.PER_MINUTE}
                  disableClear
                />
              )}
              name="duration_type"
            />
          </div>
        )}
      </div>
    </div>
  );
};
